// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import AppDialog from "@/components/common/AppDialog";
import ModalActions from "@/components/common/ModalActions";
import { FormCheckbox, FormInput, FormSelect } from "@/components/forms/FormControls";
import PhoneField from "@/components/forms/PhoneField";
import { Form } from "@/components/ui/form";
import { useAuth } from "@/lib/auth";
import { participantSchema } from "@/schemas/deals";
import { PARTICIPANT_ROLE_VALUES } from "@/constant/deals-form.constant";

/**
 * Invite someone into a deal room.
 *
 * The copy says "invite" rather than "add" because that is what now happens:
 * they receive an email and gain access on accept. It used to write a name into
 * the deal document and nothing else, so "Add participant / Save" described the
 * behaviour accurately and the behaviour was the problem.
 */
export default function ParticipantDialog({ open, onClose, onSubmit, defaultValues, roles, busy }) {
  const form = useForm({ resolver: zodResolver(participantSchema), defaultValues });
  const { context } = useAuth();
  // Invitation texts have their own switch, off by default: the A2P campaign
  // only covers users who verified their own number. See SMS_INVITES_ENABLED.
  const smsEnabled = context?.sms_invites_enabled === true;
  const sendSms = form.watch("send_sms");

  useEffect(() => {
    if (open) form.reset(defaultValues);
  }, [defaultValues, form, open]);

  return (
    <AppDialog open={open} onOpenChange={(next) => { if (!next) onClose(); }} title="Invite to this deal" eyebrow={smsEnabled ? "Email, with optional text" : "They'll get an email"} contentClassName="sm:max-w-lg" testId="deal-participant-dialog">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5" data-testid="participant-modal" noValidate>
          <FormInput name="name" label="Name" placeholder="Enter their name" data-testid="p-name" required />
          <FormInput name="email" label="Email" type="email" placeholder="name@example.com" description="We'll email them a link to join this deal." data-testid="p-email" required />
          {smsEnabled && (
            <div className="rounded-xl border border-dash-border p-4">
              <FormCheckbox
                name="send_sms"
                label="Also text them the invitation"
                description="They still get the email — the text carries the same link."
                data-testid="p-send-sms"
              />
              {sendSms && (
                <div className="mt-4">
                  <PhoneField name="phone" countryName="phone_country" required testId="p-phone" />
                  <p className="mt-2 text-xs leading-relaxed text-dash-muted">
                    Only text people who gave you this number and expect to hear from you.
                    They can reply STOP at any time. Message and data rates may apply.
                  </p>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormSelect name="role" label="Role" options={PARTICIPANT_ROLE_VALUES.map((role) => ({ value: role, label: roles[role].label }))} data-testid="p-role" required />
            <FormInput name="company" label="Company" placeholder="Enter company name" data-testid="p-company" />
          </div>
          <ModalActions onCancel={onClose} submitLabel="Send invite" busyLabel="Sending…" busy={busy} submitId="p-submit" />
        </form>
      </Form>
    </AppDialog>
  );
}
